<template lang='pug'>
  div
    p.main-text.body-1(tabindex="-1" @click="$emit('open-pose-sequences')") {{activeSequenceName}}
    v-dialog(v-model='showListDialog'
            :fullscreen="isSmallScreen"
            :hide-overlay="isSmallScreen"
            max-width="800")
      div.dialog-container
        div.d-flex.flex-row.align-baseline
          v-btn.ma-4(color='accent'
                    :disabled="!hasChanges || isManagingSequence"
                    :loading="isSavingChanges"
                    outlined
                    @click="$emit('save-changes')") {{day6Resource.save}}
          v-btn.ma-4(color='accent'
                    outlined
                    :disabled="isManagingSequence"
                    @click="onNewPoseSequence") {{dataBrowserResource.new}}
          div.flex-grow-1
          v-btn.ma-4(v-if="isSmallScreen"
                    color='accent'
                    outlined
                    @click="showListDialog=false") {{commonResource.close}}
        //- TODO Adjust heights for scrolling if this interferes with list display
        p.body-1.mb-0.pa-4(v-if="listDialogMessage") {{listDialogMessage}}
        //- TODO Test overflow and scrolling
        virtual-list.list-container(
                    :style="{ height: listHeight+'px' }"
                    :data-key="'id'"
                    :data-sources="itemData"
                    :data-component="ListItem"
                    @on-press-item='onItemPress'
                    @on-edit-item='onEditSequenceName'
                    @on-delete-item='onItemDelete')
    v-dialog(v-model='showEditDialog'
            persistent
            max-width="600"
            :fullscreen="isSmallScreen"
            :hide-overlay="isSmallScreen")
      v-card
        v-card-text
          p.pt-4.body-1 {{dataBrowserResource.updateName}}
          v-text-field(v-model="editingName"
                      color='accent'
                      filled
                      single-line
                      :rules="editNameRules"
                      :label="dataBrowserResource.name")
        v-card-actions
          v-spacer
          v-btn(outlined color='accent' @click='showEditDialog=false') {{commonResource.cancel}}
          v-btn(outlined color='accent' @click='updateName') {{day6Resource.save}}
    FullDialog(ref='fullDialog' @dialog-action='onDialogAction')
</template>

<script>
import Vue from 'vue'
import commonResource from '@/resources/common'
import day6Resource from '@/resources/day6'
import dataBrowserResource from '@/resources/dataBrowser'
import uiConstant from '@/resources/uiConstant'
import VirtualList from 'vue-virtual-scroll-list'
import ListItem from './pose-sequence-data-list-item'
import FullDialog from '../../dialogs/title-message-action-dialog'
import { characterDataRules, validatePoseSequenceName } from '@/resources/inputRules'

const deleteSequenceDialogId = 1

export default {
  name: 'CharacterPoseSequencesView',
  components: { VirtualList, FullDialog },
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    activeSequenceId: String,
    hasChanges: Boolean,
    isSavingChanges: Boolean,
    isSavingNew: Boolean,
    isSavingName: Boolean,
    isDeletingSequence: Boolean,
    isLoadingSequence: Boolean,
    nameChangingId: String,
    deletingId: String
  },
  data: function () {
    return {
      ListItem,
      commonResource,
      day6Resource,
      dataBrowserResource,
      loadingIndicatorUi: uiConstant.loadingIndicator,
      isSmallScreen: this.isSmallScreenVuetify(this.$vuetify),
      showListDialog: false,
      listDialogMessage: '',
      listHeight: window.innerHeight - 132,
      animationNameIdMap: {},
      hasConflictingNames: false,
      itemData: [],
      activeSequenceName: '',
      showEditDialog: false,
      editingItem: null,
      editingName: '',
      editNameRules: [
        ...characterDataRules.poseSequenceName,
        s => this.isNameAvailable(s, true) ? true : dataBrowserResource.poseSequenceNameIsUsed
      ]
    }
  },
  computed: {
    isManagingSequence () {
      return this.isSavingChanges ||
             this.isSavingNew ||
             this.isDeletingSequence ||
             this.isLoadingSequence
    }
  },
  watch: {
    value () {
      this._setDefaultData()
      this.syncState()
    },
    activeSequenceId (val, previous) {
      // Assumes itemData is up to date

      const emptyItems = []
      let selectedItem
      for (const item of this.itemData) {
        if (item.id) {
          item.isSelected = item.id === val
          selectedItem = item
        } else {
          emptyItems.push(item)
        }
      }

      // Update previously unsaved item with new state
      if (!selectedItem && !previous && emptyItems.length) {
        const emptyItem = emptyItems[0]
        emptyItem.id = val
        emptyItem.isSelected = true
        if (emptyItems.length > 1) {
          Vue.logger.error(new Error(`${emptyItems.length}/${this.itemData.length} pose sequences are empty`))
        }
      }

      this.updateActiveSequenceName()
    },
    showListDialog (val) {
      if (!val) {
        this.$emit('on-close-view')
      }
    }
  },
  mounted () {
    this._setDefaultData()
    this.syncState()
  },
  methods: {
    _setDefaultData () {
      // This can be called consecutively resulting in a default name with index>1.
      // Don't try and correct it as it is due to multiple consecutive async signals.
      // It's a default name and is expected to change doesn't matter what the default index is.
      if (!(this.value && Object.keys(this.value).length)) {
        this.$emit('input', { '': { name: this.getUniqueName() } })
      }
    },
    openPoseSequences () {
      this.showListDialog = true
    },
    closeView () {
      this.showEditDialog = false
      this.showListDialog = false
    },
    syncState () {
      // Avoid interleaving item data build up
      if (this._isSyncingState) { return }
      this._isSyncingState = true

      // Set underlying states itemData and animationNameIdMap

      const data = this.value
      const copy = []
      const animationNameIdMap = {}
      for (const id in data) {
        const name = data[id].name || ''
        if (!(name in animationNameIdMap)) {
          copy.push(this.makeItemData(id, name))
          animationNameIdMap[name] = id
        }
      }
      this.animationNameIdMap = animationNameIdMap
      // TODO It should possible to sort by tsMod as well. Add sorting later if works better.
      copy.sort((a, b) => a.name.localeCompare(b.name))

      this.itemData.splice(0, this.itemData.length)
      for (const entry of copy) {
        this.itemData.push(entry)
      }

      this._isSyncingState = false

      this.updateActiveSequenceName()
    },
    updateActiveSequenceName () {
      // Making computed requires additional logic to toggle changes to expectations. Less code is necessary when the property is manually updated.
      this.activeSequenceName = this.getActiveSequenceName()
    },
    getActiveSequenceName () {
      return this.activeSequenceId in this.value
        ? this.value[this.activeSequenceId].name
        : ''
    },
    getUniqueName () {
      const count = Object.keys(this.animationNameIdMap).length
      for (let i = 0; i <= count; i++) {
        const name = `pose-animation-${count + i + 1}`
        if (this.isNameAvailable(name)) {
          return name
        }
      }
      return ''
    },
    isNameAvailable (name, isEditing) {
      name = name || ''

      if (isEditing && name === this.editingItem.name) { return true }

      return !(name in this.animationNameIdMap)
    },
    makeItemData (id, name) {
      return {
        id,
        name,
        getSelectedId: () => this.activeSequenceId,
        disableActions: () => this.isManagingSequence,
        getNameChangingId: () => this.nameChangingId,
        getDeletingId: () => this.deletingId
      }
    },
    onNewPoseSequence () {
      this.showListDialog = false
      this.$emit('new-pose-sequence')
    },
    onItemPress ({ item }) {
      if (this.isManagingSequence) { return }

      if (item.id && item.id !== this.activeSequenceId) {
        this.$emit('load-pose-sequence', item)
      }
      this.showListDialog = false
    },
    onEditSequenceName ({ item }) {
      // Save single name at a time for simpler management
      if (this.isSavingName) { return }

      this.editingItem = item
      this.editingName = item.name
      this.showEditDialog = true
    },
    updateName () {
      const editingName = this.editingName.trim()
      if (!validatePoseSequenceName(editingName)) { return }
      if (!this.isNameAvailable(editingName, true)) { return }

      if (this.editingItem.name !== editingName) {
        this.$emit('update-pose-sequence-name', {
          id: this.editingItem.id,
          name: editingName
        })
      }

      this.showEditDialog = false
    },

    onItemDelete ({ item }) {
      if (this.isDeletingSequence) { return }

      this._deletingSequenceId = item.id
      this.$refs.fullDialog.showDialogProperties({
        message: dataBrowserResource.confirmDeleteAnimation(item),
        positiveAction: commonResource.yes,
        negativeAction: commonResource.no,
        id: deleteSequenceDialogId
      })
    },
    async completeDeleteSequence () {
      if (!this._deletingSequenceId) { return }

      this.$emit('delete-pose-sequence', this._deletingSequenceId)

      this._deletingSequenceId = ''
    },
    getAfterDeleteId (idDeleting) {
      let deleteIndex = -1
      const itemCount = this.itemData.length
      for (let i = 0; i < itemCount; i++) {
        const item = this.itemData[i]
        if (item.id === idDeleting) {
          deleteIndex = i
          break
        }
      }

      // Deleting not found
      if (deleteIndex < 0) {
        if (itemCount > 0) {
          return this.itemData[0].id
        }
      } else {
        // Deleting found
        if (itemCount > 1) {
          const adjacentIndex = deleteIndex === itemCount - 1 ? deleteIndex - 1 : deleteIndex + 1
          return this.itemData[adjacentIndex].id
        }
      }

      return ''
    },

    onDialogAction (dialogData) {
      switch (dialogData.id) {
        case deleteSequenceDialogId:
          if (dialogData.action > 0) {
            this.completeDeleteSequence()
          } else {
            this._deletingSequenceId = ''
          }
          break
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-container {
  background-color: white;
  overflow: hidden;
}

.list-container {
  overflow-y: auto;
}

.main-text {
  margin: 0;
  // TODO Use common values
  padding: 16px;
  background: white;
  border-radius: 4px;
  cursor: pointer;
}
</style>
